import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { FormComponent, UiComponent } from 'components';
import { UserGroupQuery } from 'queries';
import useModal from 'hooks/useModal';
import { USER_MENU_URL } from 'data/menu';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { IUserGroup } from 'types';
import * as UserGroupStyle from '../style';

const USER_GROUP_URL = USER_MENU_URL['회원 그룹 관리'];

interface Props {
  group: IUserGroup;
}

/** 그룹명 변경·그룹 삭제 */
function Info({ group }: Props) {
  const navigate = useNavigate();
  const { handleConfirm, handleAlert } = useModal();
  const { mutateAsync: update, isPending: isUpdating } =
    UserGroupQuery.useUpdate();
  const { mutateAsync: remove } = UserGroupQuery.useDelete();
  const [name, setName] = useState(group.name);

  useEffect(() => setName(group.name), [group.name]);

  const trimmed = name.trim();

  const handleRename = async () => {
    if (!trimmed) {
      handleAlert('그룹명을 입력해주세요.');
      return;
    }
    try {
      await update({ id: group.id, name: trimmed });
      toast.success('그룹명을 변경했습니다.');
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleDelete = () =>
    handleConfirm({
      confirmContent: (
        <>
          {`'${group.name}' 그룹을 삭제하시겠습니까? 구성원 정보도 함께 삭제됩니다.`}
          {group.quickMenus.length > 0 && (
            <p css={UserGroupStyle.deleteWarning}>
              {`퀵메뉴 ${group.quickMenus.map(({ title }) => `'${title}'`).join(', ')} 의 노출 대상에서 빠집니다. 다른 노출 그룹이 없으면 아무에게도 보이지 않습니다.`}
            </p>
          )}
        </>
      ),
      yesTitle: '삭제',
      confirmNext: async () => {
        try {
          await remove(group.id);
          toast.success('회원 그룹을 삭제했습니다.');
          navigate(USER_GROUP_URL, { replace: true });
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        }
      },
    });

  return (
    <UiComponent.Box>
      <div css={UserGroupStyle.section}>
        <div css={UserGroupStyle.nameRow}>
          <label htmlFor="user-group-name">그룹명</label>
          <FormComponent.Input
            id="user-group-name"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
          />
          <UiComponent.Button
            variant="border"
            color="primary"
            disabled={isUpdating || !trimmed || trimmed === group.name}
            onClick={handleRename}
          >
            변경
          </UiComponent.Button>
        </div>
        <p css={UserGroupStyle.meta}>
          {`회원 ${group.memberCount.toLocaleString('ko')}명 · 생성 ${dayjs(group.createdAt).format('YYYY-MM-DD')}${group.createdBy ? ` · ${group.createdBy}` : ''}`}
        </p>
        <div css={UserGroupStyle.footer}>
          <UiComponent.Button
            variant="border"
            color="error"
            onClick={handleDelete}
          >
            그룹 삭제
          </UiComponent.Button>
        </div>
      </div>
    </UiComponent.Box>
  );
}

export default Info;
